import { API, graphqlOperation } from "aws-amplify";
import {
  invoiceServiceByService,
  rulesByService,
  serviceByAccountByActiveByName,
} from "../../../graphql/queries";
import { getServiceName } from "../../../graphql/service/custom-queries";
import {
  createService,
  deleteService,
  updateService,
} from "../../../graphql/mutations";

import {
  MAX_ROWS_UPLOAD,
  ServiceStatus,
  ServiceType,
  UnitOptions,
  UnitToColumn,
} from "../../../utils/constants";
import { logError } from "../../../utils/appUtils";

export const getDefaultTaxRate = (parentRecord) => {
  return (
    parentRecord?.taxRates?.[0] ||
    window.appUser?.reloadYard?.taxRates?.[0] ||
    null
  );
};

export const fetchServices = async (
  accountId,
  activeOnly = true,
  availableOnly = false,
) => {
  if (!accountId) throw new Error("Account ID is required");
  let allItems = [];
  let nextToken = null;

  do {
    const input = {
      accountId: accountId,
      ...(activeOnly && {
        activeName: { beginsWith: { active: "true" } },
      }),
      limit: MAX_ROWS_UPLOAD,
      nextToken,
    };
    const resp = await API.graphql(
      graphqlOperation(serviceByAccountByActiveByName, input),
    );
    const serviceRateData =
      resp.data.serviceByAccountByActiveByName.items.filter((service) =>
        availableOnly ? service.status === ServiceStatus.AVAILABLE : true,
      );
    allItems = [...allItems, ...serviceRateData];
    nextToken = resp.data.serviceByAccountByActiveByName.nextToken;
  } while (nextToken);
  return allItems;
};

/**
 * Get unit price from service for a given billing UOM
 * @param {Object} service - Service object
 * @param {string} billingUOM - Billing unit of measure
 * @param {any} [fallback=null] - Value to return if no price is found
 * @returns {number|null} Unit price
 */
export const getUnitPrice = (service, billingUOM, fallback = null) => {
  if (!service || !billingUOM) return fallback;

  const column = UnitToColumn[billingUOM];
  if (!column) return fallback;

  const price = service[column];
  return price != null ? price : fallback;
};

export const composeNewService = (item, account) => {
  const defaultTaxRate = getDefaultTaxRate(window.appUser?.reloadYard);
  const input = {
    active: "true",
    groups: account.groups,
    accountId: account.id,
    accountName: account.businessName,
    reloadYardId: account.reloadYardId || item.reloadYardId,
  };
  // History will be logged via logHistory after successful creation

  input.name = item["name"];
  input.description = item["description"] || "";
  input.status = item["status"] || ServiceStatus.AVAILABLE;

  const taxRate = item["taxRate"];
  if (typeof taxRate === "object") {
    // manual form entry
    input.taxRate = taxRate || defaultTaxRate;
  } else {
    // CSV import
    // Recombine tax rate object from separate columns
    const taxRateValue = parseFloat(item["taxRate"]) || 0;
    const taxCode = item["taxCode"] || "";

    if (taxCode) {
      const taxRateObject = window.appUser?.reloadYard?.taxRates?.find(
        (rate) => rate.taxCode === taxCode && rate.taxRate === taxRateValue,
      );
      input.taxRate = taxRateObject || defaultTaxRate;
    } else {
      input.taxRate = defaultTaxRate;
    }
  }

  // Add unit rate fields
  UnitOptions.forEach((unit) => {
    const unitValue = item[unit.column];
    input[unit.column] = unitValue ? parseFloat(unitValue) : 0;
  });

  return input;
};

// parent record can be an account or a reloadYard
export const createAccessorialCharges = async (parentRecord) => {
  try {
    const defaultTaxRate = getDefaultTaxRate(parentRecord);
    // Create yard-level Accessorial Charges service
    const input = {
      active: "true",
      groups: parentRecord.groups,
      reloadYardId: parentRecord.reloadYardId || parentRecord.id,
      accountId: parentRecord.id, // sentinel value for template yard services
      accountName: "",
      name: ServiceType.ACCESSORIAL_CHARGES,
      description: "",
      status: ServiceStatus.AVAILABLE,
      taxRate: defaultTaxRate,
    };

    // Add unit rate fields
    UnitOptions.forEach((unit) => (input[unit.column] = 0));

    await API.graphql(graphqlOperation(createService, { input }));
  } catch (err) {
    logError("ServiceUtils:createAccessorialCharges", err, false);
  }
};

export const ServiceDeleteAction = {
  HARD: "hard",
  SOFT: "soft",
  BLOCKED: "blocked",
};

/**
 * Look up charge rules pointing at a service.
 *
 * `hasActive` blocks deletion outright - the rule would lose its rate.
 * `hasAny` (soft-deleted rules included) downgrades a hard delete to a soft
 * one: those rules still resolve `chargeRule.service` on open shipments, and
 * removing the Service row would null it and drop the line item from the
 * invoice.
 */
export const fetchChargeRuleUsage = async (serviceId) => {
  const [activeResp, anyResp] = await Promise.all([
    API.graphql(
      graphqlOperation(rulesByService, {
        serviceId,
        activeEvent: { beginsWith: { active: "true" } },
        limit: 1,
      }),
    ),
    API.graphql(graphqlOperation(rulesByService, { serviceId, limit: 1 })),
  ]);

  return {
    hasActive: activeResp.data.rulesByService.items.length > 0,
    hasAny: anyResp.data.rulesByService.items.length > 0,
  };
};

const fetchInvoiceUsage = async (serviceId) => {
  const resp = await API.graphql(
    graphqlOperation(invoiceServiceByService, { serviceId, limit: 1 }),
  );
  return resp.data?.invoiceServiceByService?.items?.length > 0;
};

/**
 * Decide how a service may be removed, mirroring the single-service delete
 * button so bulk and one-at-a-time deletes stay in step.
 *
 * Hard delete is only allowed when nothing references the service, so a
 * dangling `serviceId` can never be left behind on a ChargeRule or an
 * InvoiceService line.
 */
export const classifyServiceForDelete = async (service) => {
  if (service?.name === ServiceType.ACCESSORIAL_CHARGES)
    return {
      action: ServiceDeleteAction.BLOCKED,
      reason: "Accessorial Charges is protected",
    };

  const [hasInvoices, ruleUsage] = await Promise.all([
    fetchInvoiceUsage(service.id),
    fetchChargeRuleUsage(service.id),
  ]);

  if (ruleUsage.hasActive)
    return {
      action: ServiceDeleteAction.BLOCKED,
      reason: "used in billing rules",
    };

  return {
    action:
      !hasInvoices && !ruleUsage.hasAny
        ? ServiceDeleteAction.HARD
        : ServiceDeleteAction.SOFT,
  };
};

/**
 * The service's name as stored right now.
 *
 * Callers hand `bulkDeleteServices` rows out of the services tab's list, which
 * is fetched when the tab mounts and can sit there through a rename made
 * elsewhere - another browser tab, another user, or this tab's own edit dialog
 * before a refresh. Since a soft delete has to write `name` back (below), the
 * snapshot's copy would overwrite the newer one.
 *
 * Throws when the row is gone, so the deletion is reported as failed rather
 * than attempted against a name nobody can vouch for.
 */
const fetchCurrentServiceName = async (serviceId) => {
  const resp = await API.graphql(
    graphqlOperation(getServiceName, { id: serviceId }),
  );
  const current = resp.data?.getService;
  if (!current) throw new Error(`Service ${serviceId} no longer exists`);
  return current.name;
};

/**
 * Remove one service the only way it may be removed, and say which way that
 * was.
 *
 * Both delete paths run through here - the services tab's bulk button and the
 * edit form's Delete. They used to be separate implementations, and the copies
 * drifted: the single one wrote the caller's snapshot of `name` and reverted
 * concurrent renames long after the bulk one stopped doing that.
 */
export const deleteOneService = async (service) => {
  const classification = await classifyServiceForDelete(service);
  if (classification.action === ServiceDeleteAction.BLOCKED)
    return classification;

  if (classification.action === ServiceDeleteAction.HARD) {
    await API.graphql(
      graphqlOperation(deleteService, { input: { id: service.id } }),
    );
    return classification;
  }

  // `name` rides along because it belongs to both composite sort keys
  // (active#name and status#name) and this update touches both members;
  // AppSync rejects a partial composite key update. It is re-read here rather
  // than taken from the caller's row: the resolver rebuilds both keys from
  // whatever name arrives, so a stale one reverts the rename and moves the
  // service in every index that sorts by it.
  const currentName = await fetchCurrentServiceName(service.id);
  await API.graphql(
    graphqlOperation(updateService, {
      input: {
        id: service.id,
        name: currentName,
        active: "false",
        status: ServiceStatus.UN_AVAILABLE,
      },
      // A rename landing between that read and this write would be reverted
      // the same way. The condition turns that race into a rejected mutation
      // the caller reports as a failure, never a quiet overwrite.
      condition: { name: { eq: currentName } },
    }),
  );
  return classification;
};

// Each service costs three reads plus a write, so walk the selection in small
// batches rather than firing the whole list at AppSync at once.
const DELETE_BATCH_SIZE = 5;

/**
 * Delete a selection of services, classifying each one first.
 *
 * Returns the outcome split by what actually happened - callers own the
 * history entries and notifications.
 */
export const bulkDeleteServices = async (services = []) => {
  const targets = (services || []).filter((service) => service?.id);
  const result = { hardDeleted: [], softDeleted: [], blocked: [], failed: [] };
  if (!targets.length) return result;

  for (let i = 0; i < targets.length; i += DELETE_BATCH_SIZE) {
    const batch = targets.slice(i, i + DELETE_BATCH_SIZE);
    const outcomes = await Promise.allSettled(batch.map(deleteOneService));

    outcomes.forEach((outcome, index) => {
      const service = batch[index];
      if (outcome.status === "rejected") {
        result.failed.push({ service, reason: outcome.reason });
        return;
      }

      const { action, reason } = outcome.value;
      if (action === ServiceDeleteAction.BLOCKED)
        result.blocked.push({ service, reason });
      else if (action === ServiceDeleteAction.HARD)
        result.hardDeleted.push(service);
      else result.softDeleted.push(service);
    });
  }

  return result;
};
